/**
 * Toolbar Component - Main Control Bar for BattleRoom
 * 
 * This component provides the top toolbar that contains all the essential controls
 * for managing the virtual tabletop session. It's the command center for DMs and players.
 * 
 * MAIN CONTROL SECTIONS:
 * - Home Button: Return to Dashboard to manage characters/monsters/sessions
 * - Layer Controls: Switch between Map/Token/GM layers, show/hide/lock layers
 * - Asset Manager: Upload new maps and tokens to the battle map
 * - Grid Controls: Toggle grid visibility, adjust grid size, enable/disable snapping
 * - Zoom Controls: Zoom in/out/reset view of the battle map
 * - Session Management: Save current battle state, load previous sessions
 * 
 * KEY FEATURES:
 * - Grid Toggle: Show/hide the grid overlay (useful for gridless maps)
 * - Snap Toggle: Enable/disable token snapping to grid intersections
 * - Grid Size Slider: Adjust grid square size from 25px to 100px
 * - Zoom Display: Shows current zoom percentage (e.g., "69%")
 * - Save Session: Downloads current battle state as JSON file
 * - Load Session: Upload and restore a previously saved battle state
 * 
 * SMART BEHAVIOR:
 * - Grid controls are grouped together for logical workflow
 * - Save/Load buttons provide user feedback with tooltips
 * - Asset Manager is compact but provides full upload functionality
 * - Layer Controls integrate seamlessly with the overall layer system
 * 
 * INTEGRATION:
 * - Receives all state from useBattleRoom hook
 * - Calls appropriate handlers when users interact with controls
 * - Works with LayerControls and AssetManager sub-components
 * - Styled to match the overall Roll20-style dark theme
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import LayerControls from '../../LayerControls/LayerControls';
import AssetManager from '../../AssetManager/AssetManager';
import { Ruler as RulerLucide } from 'lucide-react';
import FogControls from './FogControls';
import GridFlyout from './GridFlyout';
import DrawingToolsPanel from './DrawingToolsPanel';
import MovementModeControls from './MovementModeControls';
import './Toolbar.css';

const HomeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

/**
 * Toolbar component that provides controls for the battle map
 * @param {Function} onGridToggle - Toggle grid visibility
 * @param {Function} onSnapToggle - Toggle snap to grid
 * @param {Function} onGridSizeChange - Change grid size
 * @param {Function} onZoomIn - Zoom in
 * @param {Function} onZoomOut - Zoom out
 * @param {Function} onZoomReset - Reset zoom
 * @param {Function} onSaveSession - Save the current session
 * @param {Function} onLoadSession - Load a saved session
 * @param {boolean} showGrid - Whether grid is currently visible
 * @param {boolean} snapToGrid - Whether snap to grid is enabled
 * @param {number} gridSize - Current grid size
 * @param {number} zoom - Current zoom level
 */
const Toolbar = ({ 
  showGrid, 
  snapToGrid, 
  gridSize = 50,
  gridOpacity = 0.5,
  gridWidth = 40,
  gridHeight = 30,
  zoom = 1,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  showTokenLabels = true,
  onToggleTokenLabels,
  onGridToggle, 
  onSnapToggle,
  onGridSizeChange,
  onGridOpacityChange,
  onGridWidthChange,
  onGridHeightChange,
  // Layer control props
  layers,
  activeLayer,
  onActiveLayerChange,
  onLayerVisibilityChange,
  onLayerLockChange,
  // Asset management props
  onAssetUpload,
  onOpenAssetBrowser,
  onOpenSceneManager,
  sceneManagerAvailable = false,
  // Measurement tool
  measurementActive = false,
  onToggleMeasurement,
  // Shared map ping
  sharedPingActive = false,
  sharedPingAvailable = false,
  onToggleSharedPing,
  movementAvailable = false,
  movementMode = 'walk',
  movementIntent = 'auto',
  combatActive = false,
  onMovementModeChange,
  onMovementIntentChange,
  // Combat tools removed from toolbar per 2025-09-16 spec
  // Fog of War (GM-only)
  isGameMaster = false,
  fogOfWar,
  onFogToggle,
  onFogOpacityChange,
  onFogIgnoreGMToggle,
  onFogRevealAll,
  onFogCoverAll,
  fogEditMode = 'none',
  onFogModeChange,
  // GAP-003: shape palette props — date_of_change: 2026-05-20
  fogShape = 'rect',
  onFogShapeChange,
  onFogUndo,
  canUndoFog = false,
  fogControlsOpen = false,
  onToggleFogControls,
  onCloseFogControls,
  visionControlsOpen = false,
  visionPreviewEnabled = false,
  onToggleVisionControls,
  // GAP-002: Drawing tool props (GM-only)
  // date_of_change: 2026-05-20
  drawingEnabled = false,
  onDrawingToggle,
  drawingTool = 'freehand',
  onDrawingToolChange,
  drawingColor = '#ff0000',
  onDrawingColorChange,
  drawingLineWidth = 3,
  onDrawingLineWidthChange,
  onDrawingUndo,
  onDrawingClear,
  canUndoDrawing = false,
}) => {
  const navigate = useNavigate();
  const fogControlsRef = useRef(null);
  const fogButtonRef = useRef(null);
  const [fogAnchorRect, setFogAnchorRect] = useState(null);
  const gridButtonRef = useRef(null);
  const [gridAnchorRect, setGridAnchorRect] = useState(null);
  const [gridControlsOpen, setGridControlsOpen] = useState(false);
  // GAP-002: drawing flyout state — date_of_change: 2026-05-20
  const drawButtonRef = useRef(null);
  const [drawControlsOpen, setDrawControlsOpen] = useState(false);
  const [drawAnchorRect, setDrawAnchorRect] = useState(null);
  const movementButtonRef = useRef(null);
  const [movementControlsOpen, setMovementControlsOpen] = useState(false);
  const [movementAnchorRect, setMovementAnchorRect] = useState(null);

  // @date_of_change 2025-09-16 - Add confirm-before-leave prompt to prevent accidental loss
  const handleHomeClick = () => {
    const proceed = window.confirm('Have you saved your battle room?\n\nLeaving the Battle Room may discard unsaved changes.\n\nClick OK to go Home, or Cancel to stay.');
    if (!proceed) return;
    navigate('/');
  };

  // BattleRoom control chrome uses the shared table palette; only active spatial
  // tools receive a cool accent. date_of_change: 2026-09-08
  const buttonBaseClassName = 'battle-toolbar__button';
  const secondaryButtonClassName = `${buttonBaseClassName} battle-toolbar__button--secondary`;
  const accentButtonClassName = `${buttonBaseClassName} battle-toolbar__button--accent`;
  const controlStackClassName = 'flex flex-col gap-1 self-stretch justify-end';
  const metaLabelClassName = 'battle-toolbar__meta-label';
  const dividerClassName = 'battle-toolbar__divider';
  const assetControlsRowClassName = 'flex items-center gap-1.5';
  const groupShellClassName = 'battle-toolbar__group';
  const suppressToolbarAction = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
  };
  const getToolbarActionProps = (handler) => ({
    type: 'button',
    onMouseDown: (event) => {
      suppressToolbarAction(event);
      if (typeof handler === 'function') {
        handler(event);
      }
    },
    onClick: suppressToolbarAction
  });
  const handleOpenAssetBrowser = (event) => {
    suppressToolbarAction(event);
    if (typeof onOpenAssetBrowser === 'function') {
      onOpenAssetBrowser();
    }
  };
  const handleToggleGridControls = useCallback(() => {
    setGridControlsOpen((prev) => !prev);
    setMovementControlsOpen(false);
  }, []);
  const handleCloseGridControls = useCallback(() => {
    setGridControlsOpen(false);
  }, []);

  const syncFogAnchorRect = useCallback(() => {
    if (!fogButtonRef.current) {
      return;
    }
    const rect = fogButtonRef.current.getBoundingClientRect();
    setFogAnchorRect({
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      right: rect.right,
      width: rect.width,
      height: rect.height
    });
  }, []);

  const syncGridAnchorRect = useCallback(() => {
    if (!gridButtonRef.current) {
      return;
    }
    const rect = gridButtonRef.current.getBoundingClientRect();
    setGridAnchorRect({
      top: rect.top,
      left: rect.left,
      bottom: rect.bottom,
      right: rect.right,
      width: rect.width,
      height: rect.height
    });
  }, []);

  useEffect(() => {
    if (!fogControlsOpen) {
      return undefined;
    }

    syncFogAnchorRect();
    window.addEventListener('resize', syncFogAnchorRect);
    window.addEventListener('scroll', syncFogAnchorRect, true);
    return () => {
      window.removeEventListener('resize', syncFogAnchorRect);
      window.removeEventListener('scroll', syncFogAnchorRect, true);
    };
  }, [fogControlsOpen, syncFogAnchorRect]);

  useEffect(() => {
    if (!gridControlsOpen) {
      return undefined;
    }

    syncGridAnchorRect();
    window.addEventListener('resize', syncGridAnchorRect);
    window.addEventListener('scroll', syncGridAnchorRect, true);
    return () => {
      window.removeEventListener('resize', syncGridAnchorRect);
      window.removeEventListener('scroll', syncGridAnchorRect, true);
    };
  }, [gridControlsOpen, syncGridAnchorRect]);

  useEffect(() => {
    if (fogControlsOpen) {
      setGridControlsOpen(false);
      setDrawControlsOpen(false);
      setMovementControlsOpen(false);
    }
  }, [fogControlsOpen]);

  // GAP-002: sync draw button position for flyout — date_of_change: 2026-05-20
  const syncDrawAnchorRect = useCallback(() => {
    if (!drawButtonRef.current) return;
    const rect = drawButtonRef.current.getBoundingClientRect();
    setDrawAnchorRect({ top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right, width: rect.width, height: rect.height });
  }, []);

  useEffect(() => {
    if (!drawControlsOpen) return undefined;
    syncDrawAnchorRect();
    window.addEventListener('resize', syncDrawAnchorRect);
    window.addEventListener('scroll', syncDrawAnchorRect, true);
    return () => {
      window.removeEventListener('resize', syncDrawAnchorRect);
      window.removeEventListener('scroll', syncDrawAnchorRect, true);
    };
  }, [drawControlsOpen, syncDrawAnchorRect]);

  const syncMovementAnchorRect = useCallback(() => {
    if (!movementButtonRef.current) return;
    const rect = movementButtonRef.current.getBoundingClientRect();
    setMovementAnchorRect({ top: rect.top, left: rect.left, bottom: rect.bottom, right: rect.right, width: rect.width, height: rect.height });
  }, []);

  useEffect(() => {
    if (!movementControlsOpen) return undefined;
    syncMovementAnchorRect();
    window.addEventListener('resize', syncMovementAnchorRect);
    window.addEventListener('scroll', syncMovementAnchorRect, true);
    return () => {
      window.removeEventListener('resize', syncMovementAnchorRect);
      window.removeEventListener('scroll', syncMovementAnchorRect, true);
    };
  }, [movementControlsOpen, syncMovementAnchorRect]);

  return (
    <div className="battle-toolbar__scroll">
      <div className="battle-toolbar__deck">
        {/* Session Options moved to UserInfoTopbar (upper bar) */}

        {isGameMaster && (
          <>
            <div className={groupShellClassName}>
              <div className={controlStackClassName}>
                <span className={metaLabelClassName}>Campaign</span>
                <button
                  {...getToolbarActionProps(onOpenSceneManager)}
                  className={`${accentButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
                  disabled={!sceneManagerAvailable}
                  title={sceneManagerAvailable ? 'Create, switch, duplicate, archive, and restore campaign scenes' : 'Join a live BattleRoom to manage campaign scenes'}
                  data-testid="battle-toolbar-scenes"
                >
                  <span>Scenes</span>
                </button>
              </div>
            </div>
            <div className={groupShellClassName}>
              <div className={controlStackClassName}>
                <span className={metaLabelClassName}>Maps</span>
                <AssetManager
                  activeLayer={activeLayer}
                  onAssetUpload={onAssetUpload}
                  uiMode="map"
                  forcedLayer="map"
                  browseLabel="Library"
                  showIcons={false}
                />
              </div>
            </div>
            <div className={groupShellClassName}>
              <div className={controlStackClassName}>
                <span className={metaLabelClassName}>Tokens</span>
                <div className={assetControlsRowClassName}>
                  <button
                    {...getToolbarActionProps(handleOpenAssetBrowser)}
                    className={accentButtonClassName}
                    title="Open your hero and monster roster"
                    data-testid="battle-toolbar-add-token"
                  >
                    <span>Roster</span>
                  </button>
                  <AssetManager
                    activeLayer={activeLayer}
                    onAssetUpload={onAssetUpload}
                    uiMode="token"
                    forcedLayer="token"
                    browseLabel="Library"
                    showIcons={false}
                  />
                </div>
              </div>
            </div>
            <span className={dividerClassName} aria-hidden="true" />
          </>
        )}
        {isGameMaster && (
          <button
            {...getToolbarActionProps(onToggleVisionControls)}
            className={visionControlsOpen || visionPreviewEnabled
              ? `${buttonBaseClassName} battle-toolbar__button--active`
              : secondaryButtonClassName}
            aria-expanded={visionControlsOpen}
            title={visionPreviewEnabled
              ? 'Player preview is on. Open vision controls to review or turn it off.'
              : 'Basic line-of-sight and movement barriers (separate from Fog and Drawing)'}
            data-testid="battle-toolbar-vision"
          >
            <span>Vision</span>
          </button>
        )}

        {isGameMaster && (
          <div className={groupShellClassName}>
            <div className={controlStackClassName}>
              <span className={metaLabelClassName}>Layer</span>
              <LayerControls
                layers={layers}
                activeLayer={activeLayer}
                onActiveLayerChange={onActiveLayerChange}
                onLayerVisibilityChange={onLayerVisibilityChange}
                onLayerLockChange={onLayerLockChange}
                showIndicators={false}
                hideCompactLabel={true}
              />
            </div>
          </div>
        )}
        {/* GAP-002: Draw tools button — GM-only, opens inline flyout — date_of_change: 2026-05-20 */}
        {isGameMaster && (
          <div className="relative self-end">
            <button
              {...getToolbarActionProps(() => {
                setDrawControlsOpen((prev) => !prev);
                setMovementControlsOpen(false);
                if (!drawControlsOpen) syncDrawAnchorRect();
              })}
              ref={drawButtonRef}
              className={drawingEnabled
                ? `${buttonBaseClassName} battle-toolbar__button--draw-active`
                : (drawControlsOpen
                  ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
                  : secondaryButtonClassName)
              }
              aria-expanded={drawControlsOpen}
              aria-pressed={drawingEnabled}
              title="Drawing tools — annotate the battle map"
              data-testid="battle-toolbar-draw"
            >
              <span>Draw</span>
            </button>
            {drawControlsOpen && (
              <DrawingToolsPanel
                onClose={() => setDrawControlsOpen(false)}
                triggerRef={drawButtonRef}
                anchorRect={drawAnchorRect}
                drawingEnabled={drawingEnabled}
                onDrawingToggle={onDrawingToggle}
                drawingTool={drawingTool}
                onDrawingToolChange={onDrawingToolChange}
                drawingColor={drawingColor}
                onDrawingColorChange={onDrawingColorChange}
                drawingLineWidth={drawingLineWidth}
                onDrawingLineWidthChange={onDrawingLineWidthChange}
                onDrawingUndo={onDrawingUndo}
                onDrawingClear={onDrawingClear}
                canUndoDrawing={canUndoDrawing}
              />
            )}
          </div>
        )}
        {isGameMaster && (
          <div className="relative self-end" ref={fogControlsRef}>
            <button
              {...getToolbarActionProps(onToggleFogControls)}
              ref={fogButtonRef}
              className={fogControlsOpen || fogEditMode !== 'none'
                ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
                : secondaryButtonClassName}
              title={fogEditMode === 'none'
                ? 'Fog of War controls'
                : `${fogEditMode === 'reveal' ? 'Reveal' : 'Cover'} ${fogShape} fog tool active. Open controls.`}
              aria-expanded={fogControlsOpen}
              aria-pressed={fogEditMode !== 'none'}
              data-testid="battle-toolbar-fog"
            >
              <span>Fog</span>
              {fogEditMode !== 'none' && (
                <span className="battle-toolbar__fog-state">
                  {fogEditMode} {fogShape === 'polygon' ? 'poly' : fogShape}
                </span>
              )}
            </button>
            {fogControlsOpen && (
              <FogControls
                onClose={onCloseFogControls}
                triggerRef={fogButtonRef}
                anchorRect={fogAnchorRect}
                fogOfWar={fogOfWar}
                onFogToggle={onFogToggle}
                onFogOpacityChange={onFogOpacityChange}
                onFogIgnoreGMToggle={onFogIgnoreGMToggle}
                onFogRevealAll={onFogRevealAll}
                onFogCoverAll={onFogCoverAll}
                fogEditMode={fogEditMode}
                onFogModeChange={onFogModeChange}
                fogShape={fogShape}
                onFogShapeChange={onFogShapeChange}
                onFogUndo={onFogUndo}
                canUndoFog={canUndoFog}
              />
            )}
          </div>
        )}
        {movementAvailable && (
          <div className="relative self-end">
            <button
              {...getToolbarActionProps(() => {
                setMovementControlsOpen((prev) => !prev);
                setGridControlsOpen(false);
                setDrawControlsOpen(false);
                onCloseFogControls?.();
                if (!movementControlsOpen) syncMovementAnchorRect();
              })}
              ref={movementButtonRef}
              className={movementControlsOpen || movementIntent !== 'auto' || movementMode !== 'walk'
                ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
                : secondaryButtonClassName}
              aria-expanded={movementControlsOpen}
              title="Choose an exceptional movement mode or Game Master movement intent"
              data-testid="battle-toolbar-movement"
            >
              <span>Movement</span>
              {(movementMode !== 'walk' || movementIntent !== 'auto') && (
                <span className="battle-toolbar__movement-state">
                  {movementMode !== 'walk' ? movementMode : movementIntent}
                </span>
              )}
            </button>
            <MovementModeControls
              visible={movementControlsOpen}
              isGameMaster={isGameMaster}
              combatActive={combatActive}
              mode={movementMode}
              intent={movementIntent}
              onModeChange={onMovementModeChange}
              onIntentChange={onMovementIntentChange}
              onClose={() => setMovementControlsOpen(false)}
              triggerRef={movementButtonRef}
              anchorRect={movementAnchorRect}
            />
          </div>
        )}
        <button
          {...getToolbarActionProps(onToggleMeasurement)}
          className={measurementActive
            ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
            : secondaryButtonClassName}
          aria-pressed={!!measurementActive}
          title="Toggle measurement mode"
          data-testid="battle-toolbar-measure"
        >
          <RulerLucide size={15} />
          <span>Measure</span>
        </button>
        <button
          {...getToolbarActionProps(onToggleSharedPing)}
          className={`${sharedPingActive
            ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
            : secondaryButtonClassName} disabled:cursor-not-allowed disabled:opacity-50`}
          aria-pressed={!!sharedPingActive}
          disabled={!sharedPingAvailable}
          title={sharedPingAvailable ? 'Ping mode. You can also hold the map; Shift+hold focuses everyone.' : 'Join a live BattleRoom to place shared pings'}
          data-testid="battle-toolbar-ping"
        >
          <span>Ping</span>
        </button>

        {/* date_of_change: 2026-05-20 — Grid/snap toggles are always visible (GM + player).
             They mirror the flyout controls so E2E tests can reach them without opening the flyout. */}
        <button
          {...getToolbarActionProps(onGridToggle)}
          className={showGrid
            ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
            : secondaryButtonClassName}
          aria-pressed={!!showGrid}
          title="Toggle grid visibility"
          data-testid="battle-toolbar-grid-toggle"
        >
          <span>Grid</span>
        </button>
        <button
          {...getToolbarActionProps(onSnapToggle)}
          className={snapToGrid
            ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
            : secondaryButtonClassName}
          aria-pressed={!!snapToGrid}
          title="Toggle snap to grid"
          data-testid="battle-toolbar-snap-toggle"
        >
          <span>Snap</span>
        </button>
        <button
          type="button"
          onMouseDown={suppressToolbarAction}
          onClick={(event) => {
            suppressToolbarAction(event);
            onToggleTokenLabels?.(event);
          }}
          className={showTokenLabels
            ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
            : secondaryButtonClassName}
          aria-pressed={!!showTokenLabels}
          title={showTokenLabels
            ? 'Hide token names'
            : 'Show token names on the map'}
          data-testid="battle-toolbar-token-labels"
        >
          <span>{showTokenLabels ? 'Labels On' : 'Labels Off'}</span>
        </button>

        {/* GM-only: grid size / dimension proxies visible to tests without opening flyout */}
        {isGameMaster && (
          <>
            <input type="hidden" data-testid="battle-toolbar-grid-size" value={gridSize} readOnly />
            <input
              type="number"
              aria-label="Grid width"
              data-testid="battle-toolbar-grid-width"
              value={gridWidth}
              onChange={(e) => onGridWidthChange(e.target.value)}
              onBlur={(e) => { if (e.target.value === '' || parseInt(e.target.value, 10) < 10) onGridWidthChange(40); }}
              min="10" max="200"
              className="sr-only"
            />
            <input
              type="number"
              aria-label="Grid height"
              data-testid="battle-toolbar-grid-height"
              value={gridHeight}
              onChange={(e) => onGridHeightChange(e.target.value)}
              onBlur={(e) => { if (e.target.value === '' || parseInt(e.target.value, 10) < 10) onGridHeightChange(30); }}
              min="10" max="200"
              className="sr-only"
            />
          </>
        )}

        <div className="relative self-end">
          <button
            {...getToolbarActionProps(handleToggleGridControls)}
            ref={gridButtonRef}
            className={gridControlsOpen
              ? `${buttonBaseClassName} battle-toolbar__button--spatial-active`
              : secondaryButtonClassName}
            aria-expanded={gridControlsOpen}
            data-testid="battle-toolbar-grid-controls"
          >
            <span>Grid Tools</span>
          </button>
          {gridControlsOpen && (
            <GridFlyout
              onClose={handleCloseGridControls}
              triggerRef={gridButtonRef}
              anchorRect={gridAnchorRect}
              showGrid={showGrid}
              snapToGrid={snapToGrid}
              gridOpacity={gridOpacity}
              gridSize={gridSize}
              gridWidth={gridWidth}
              gridHeight={gridHeight}
              isGameMaster={isGameMaster}
              onGridToggle={onGridToggle}
              onSnapToggle={onSnapToggle}
              onGridOpacityChange={onGridOpacityChange}
              onGridSizeChange={onGridSizeChange}
              onGridWidthChange={onGridWidthChange}
              onGridHeightChange={onGridHeightChange}
            />
          )}
        </div>

        <div className={`${controlStackClassName} battle-toolbar__zoom`} role="group" aria-label="Map zoom controls">
          <span className={metaLabelClassName}>Zoom</span>
          <div className="battle-toolbar__zoom-controls">
            <button
              {...getToolbarActionProps(onZoomOut)}
              className="battle-toolbar__zoom-button"
              title="Zoom out"
              aria-label="Zoom out"
              data-testid="battle-toolbar-zoom-out"
            >
              <span aria-hidden="true">-</span>
            </button>
            <button
              {...getToolbarActionProps(onZoomReset)}
              className="battle-toolbar__zoom-readout"
              title="Fit map to viewport"
              aria-label="Fit map to viewport"
              data-testid="battle-toolbar-zoom-reset"
            >
              <span data-testid="battle-toolbar-zoom-readout">{Math.round(zoom * 100)}%</span>
            </button>
            <button
              {...getToolbarActionProps(onZoomIn)}
              className="battle-toolbar__zoom-button"
              title="Zoom in"
              aria-label="Zoom in"
              data-testid="battle-toolbar-zoom-in"
            >
              <span aria-hidden="true">+</span>
            </button>
          </div>
        </div>

        <button
          onClick={handleHomeClick}
          className={secondaryButtonClassName}
          title="🏠 Return to Dashboard - Manage combat simulations, characters, monsters, and rules"
          data-testid="battle-toolbar-dashboard"
        >
          <HomeIcon className="w-4 h-4" />
          <span className="hidden xl:inline">Dashboard</span>
        </button>
      </div>
    </div>
  );
};

Toolbar.propTypes = {
  // Grid and snap controls
  showGrid: PropTypes.bool.isRequired,
  snapToGrid: PropTypes.bool.isRequired,
  gridSize: PropTypes.number,
  gridOpacity: PropTypes.number,
  gridWidth: PropTypes.number,
  gridHeight: PropTypes.number,
  onGridToggle: PropTypes.func.isRequired,
  onSnapToggle: PropTypes.func.isRequired,
  onGridSizeChange: PropTypes.func.isRequired,
  onGridOpacityChange: PropTypes.func.isRequired,
  onGridWidthChange: PropTypes.func.isRequired,
  onGridHeightChange: PropTypes.func.isRequired,
  
  // Zoom controls
  zoom: PropTypes.number,
  onZoomIn: PropTypes.func.isRequired,
  onZoomOut: PropTypes.func.isRequired,
  onZoomReset: PropTypes.func.isRequired,
  showTokenLabels: PropTypes.bool,
  onToggleTokenLabels: PropTypes.func,
  
  // Session controls
  onSaveSession: PropTypes.func.isRequired,
  onLoadSession: PropTypes.func.isRequired,
  
  // Layer controls
  layers: PropTypes.object,
  activeLayer: PropTypes.string,
  onActiveLayerChange: PropTypes.func,
  onLayerVisibilityChange: PropTypes.func,
  onLayerLockChange: PropTypes.func,
  
  // Asset management
  onAssetUpload: PropTypes.func.isRequired,
  onOpenAssetBrowser: PropTypes.func.isRequired,
  onOpenSceneManager: PropTypes.func,
  sceneManagerAvailable: PropTypes.bool,

  // Measurement tool
  measurementActive: PropTypes.bool,
  onToggleMeasurement: PropTypes.func.isRequired,
  sharedPingActive: PropTypes.bool,
  sharedPingAvailable: PropTypes.bool,
  onToggleSharedPing: PropTypes.func,
  movementAvailable: PropTypes.bool,
  movementMode: PropTypes.string,
  movementIntent: PropTypes.string,
  combatActive: PropTypes.bool,
  onMovementModeChange: PropTypes.func,
  onMovementIntentChange: PropTypes.func,
  
  // Combat tools removed from toolbar per 2025-09-16 spec
  // Fog of War props
  isGameMaster: PropTypes.bool,
  fogOfWar: PropTypes.shape({
    enabled: PropTypes.bool,
    opacity: PropTypes.number,
    ignoreGM: PropTypes.bool,
    operations: PropTypes.array,
  }),
  onFogToggle: PropTypes.func,
  onFogOpacityChange: PropTypes.func,
  onFogIgnoreGMToggle: PropTypes.func,
  onFogRevealAll: PropTypes.func,
  onFogCoverAll: PropTypes.func,
  fogEditMode: PropTypes.oneOf(['none', 'reveal', 'cover']),
  onFogModeChange: PropTypes.func,
  fogControlsOpen: PropTypes.bool,
  onToggleFogControls: PropTypes.func,
  onCloseFogControls: PropTypes.func,
  visionControlsOpen: PropTypes.bool,
  visionPreviewEnabled: PropTypes.bool,
  onToggleVisionControls: PropTypes.func,
  // GAP-002: Drawing props \u2014 date_of_change: 2026-05-20
  drawingEnabled: PropTypes.bool,
  onDrawingToggle: PropTypes.func,
  drawingTool: PropTypes.string,
  onDrawingToolChange: PropTypes.func,
  drawingColor: PropTypes.string,
  onDrawingColorChange: PropTypes.func,
  drawingLineWidth: PropTypes.number,
  onDrawingLineWidthChange: PropTypes.func,
  onDrawingUndo: PropTypes.func,
  onDrawingClear: PropTypes.func,
  canUndoDrawing: PropTypes.bool,
};

export default Toolbar;
